
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./VendorAdvertisements.css";

const API_BASE = "https://bmtvendorvendorfinal.onrender.com";
const API_URL = `${API_BASE}/api/vendor/advertisements`;

const INITIAL_FORM = {
  title: "",
  description: "",
  adType: "Property",
  budget: "",
  startDate: "",
  endDate: "",
};

const TYPES = ["Property", "Hotel", "Cab", "Tour", "Activity", "Event"];

function getToken() {
  return localStorage.getItem("vendorToken");
}

async function apiRequest(path = "", options = {}) {
  const token = getToken();

  if (!token) {
    throw new Error("Vendor login session nahi mila. Dobara login karo.");
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok || data.success === false) {
    throw new Error(data.message || `Request failed (${response.status})`);
  }

  return data;
}

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatMoney(value) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);
}

function getStatusClass(status = "") {
  return status.toLowerCase().replace(/\s+/g, "-");
}

export default function VendorAdvertisements() {
  const [ads, setAds] = useState([]);
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [showForm, setShowForm] = useState(false);

  const loadAds = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await apiRequest();
      setAds(Array.isArray(data.advertisements) ? data.advertisements : []);
    } catch (err) {
      setError(err.message || "Advertisements load nahi ho paaye.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAds();
  }, [loadAds]);

  const stats = useMemo(() => {
    return {
      total: ads.length,
      active: ads.filter((ad) => ad.status === "Active").length,
      pending: ads.filter((ad) => ad.status === "Pending").length,
      budget: ads.reduce((sum, ad) => sum + (Number(ad.budget) || 0), 0),
    };
  }, [ads]);

  const filteredAds = useMemo(() => {
    const query = search.trim().toLowerCase();

    return ads.filter((ad) => {
      const matchesSearch =
        !query ||
        ad.title?.toLowerCase().includes(query) ||
        ad.adType?.toLowerCase().includes(query) ||
        ad.campaignName?.toLowerCase().includes(query);

      const matchesStatus =
        statusFilter === "All" || ad.status === statusFilter;

      const matchesType = typeFilter === "All" || ad.adType === typeFilter;

      return matchesSearch && matchesStatus && matchesType;
    });
  }, [ads, search, statusFilter, typeFilter]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitAdvertisement = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (form.endDate < form.startDate) {
      setError("End date, start date se pehle nahi ho sakti.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        adType: form.adType,
        budget: Number(form.budget),
        startDate: form.startDate,
        endDate: form.endDate,
      };

      const data = await apiRequest("", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (data.advertisement) {
        setAds((prev) => [
          data.advertisement,
          ...prev.filter((ad) => ad._id !== data.advertisement._id),
        ]);
      } else {
        await loadAds();
      }

      setForm(INITIAL_FORM);
      setShowForm(false);
      setSuccess(data.message || "Advertisement successfully submitted.");
    } catch (err) {
      setError(err.message || "Advertisement create nahi hua.");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (ad) => {
    const nextStatus = ad.status === "Active" ? "Paused" : "Active";

    if (nextStatus === "Active" && ad.status !== "Paused") {
      setError("Advertisement ko active karne se pehle admin approval zaroori hai.");
      return;
    }

    setError("");
    setSuccess("");
    setBusyId(ad._id);

    try {
      const data = await apiRequest(`/${ad._id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: nextStatus }),
      });

      if (data.advertisement) {
        setAds((prev) =>
          prev.map((item) =>
            item._id === ad._id ? data.advertisement : item
          )
        );
      } else {
        await loadAds();
      }

      setSuccess(data.message || "Advertisement status updated.");
    } catch (err) {
      setError(err.message || "Status update nahi ho saka.");
    } finally {
      setBusyId("");
    }
  };

  const deleteAdvertisement = async (ad) => {
    const confirmed = window.confirm(
      `Kya tum "${ad.title}" advertisement delete karna chahte ho?`
    );

    if (!confirmed) return;

    setError("");
    setSuccess("");
    setBusyId(ad._id);

    try {
      const data = await apiRequest(`/${ad._id}`, { method: "DELETE" });
      setAds((prev) => prev.filter((item) => item._id !== ad._id));
      setSuccess(data.message || "Advertisement deleted.");
    } catch (err) {
      setError(err.message || "Advertisement delete nahi hua.");
    } finally {
      setBusyId("");
    }
  };

  return (
    <main className="va-page">
      <header className="va-header">
        <div>
          <div className="va-eyebrow">
            <span className="va-eyebrow-dot" />
            MARKETING CENTER
          </div>
          <h1>Advertisements</h1>
          <p className="va-subtitle">
            Apne business ko promote karo aur apni advertising campaigns manage karo.
          </p>
        </div>

        <button
          type="button"
          className="va-primary-btn"
          onClick={() => {
            setShowForm((value) => !value);
            setError("");
            setSuccess("");
          }}
        >
          <span className="va-plus">{showForm ? "−" : "+"}</span>
          {showForm ? "Close Form" : "Create Advertisement"}
        </button>
      </header>

      {error && (
        <div className="va-alert va-alert-error" role="alert">
          <span>!</span>
          <div>{error}</div>
          <button onClick={() => setError("")} aria-label="Dismiss error">×</button>
        </div>
      )}

      {success && (
        <div className="va-alert va-alert-success" role="status">
          <span>✓</span>
          <div>{success}</div>
          <button onClick={() => setSuccess("")} aria-label="Dismiss message">×</button>
        </div>
      )}

      <section className="va-stats">
        <article className="va-stat-card">
          <div className="va-stat-top">
            <span className="va-stat-icon va-icon-purple">▤</span>
            <span className="va-stat-caption">ALL CAMPAIGNS</span>
          </div>
          <div className="va-stat-value">{stats.total}</div>
          <div className="va-stat-bottom">Total advertisements</div>
        </article>

        <article className="va-stat-card">
          <div className="va-stat-top">
            <span className="va-stat-icon va-icon-green">↗</span>
            <span className="va-stat-caption">LIVE CAMPAIGNS</span>
          </div>
          <div className="va-stat-value">{stats.active}</div>
          <div className="va-stat-bottom">Currently active</div>
        </article>

        <article className="va-stat-card">
          <div className="va-stat-top">
            <span className="va-stat-icon va-icon-orange">◷</span>
            <span className="va-stat-caption">AWAITING REVIEW</span>
          </div>
          <div className="va-stat-value">{stats.pending}</div>
          <div className="va-stat-bottom">Pending admin approval</div>
        </article>

        <article className="va-stat-card">
          <div className="va-stat-top">
            <span className="va-stat-icon va-icon-blue">₹</span>
            <span className="va-stat-caption">TOTAL BUDGET</span>
          </div>
          <div className="va-stat-value va-budget-value">
            {formatMoney(stats.budget)}
          </div>
          <div className="va-stat-bottom">Combined campaign budgets</div>
        </article>
      </section>

      {showForm && (
        <section className="va-panel va-form-panel">
          <div className="va-panel-heading">
            <div>
              <span className="va-section-kicker">NEW CAMPAIGN</span>
              <h2>Create an advertisement</h2>
              <p>Campaign details fill karo aur approval ke liye submit karo.</p>
            </div>
            <span className="va-form-icon">✦</span>
          </div>

          <form onSubmit={submitAdvertisement}>
            <div className="va-form-grid">
              <label className="va-field va-field-full">
                <span>Campaign title <b>*</b></span>
                <input
                  name="title"
                  value={form.title}
                  onChange={updateField}
                  placeholder="e.g. Summer Holiday Special"
                  maxLength={120}
                  required
                />
              </label>

              <label className="va-field va-field-full">
                <span>Description</span>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={updateField}
                  placeholder="Describe what you want to promote..."
                  rows={3}
                  maxLength={1000}
                />
              </label>

              <label className="va-field">
                <span>Advertisement type <b>*</b></span>
                <select
                  name="adType"
                  value={form.adType}
                  onChange={updateField}
                  required
                >
                  {TYPES.map((type) => (
                    <option value={type} key={type}>{type}</option>
                  ))}
                </select>
              </label>

              <label className="va-field">
                <span>Total budget (₹) <b>*</b></span>
                <div className="va-input-prefix">
                  <span>₹</span>
                  <input
                    type="number"
                    name="budget"
                    value={form.budget}
                    onChange={updateField}
                    min="1"
                    step="1"
                    placeholder="Enter your budget"
                    required
                  />
                </div>
              </label>

              <label className="va-field">
                <span>Campaign start date <b>*</b></span>
                <input
                  type="date"
                  name="startDate"
                  value={form.startDate}
                  onChange={updateField}
                  max={form.endDate || undefined}
                  required
                />
              </label>

              <label className="va-field">
                <span>Campaign end date <b>*</b></span>
                <input
                  type="date"
                  name="endDate"
                  value={form.endDate}
                  onChange={updateField}
                  min={form.startDate || undefined}
                  required
                />
              </label>
            </div>

            <div className="va-form-note">
              <span>ⓘ</span>
              <p>
                Submit karne ke baad campaign <strong>Pending</strong> status
                mein rahega jab tak admin approval na de.
              </p>
            </div>

            <div className="va-form-actions">
              <button
                type="button"
                className="va-secondary-btn"
                onClick={() => {
                  setShowForm(false);
                  setForm(INITIAL_FORM);
                }}
              >
                Cancel
              </button>
              <button type="submit" className="va-primary-btn" disabled={saving}>
                {saving ? <><span className="va-spinner" /> Submitting...</> : "Submit for Approval →"}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="va-panel va-list-panel">
        <div className="va-panel-heading va-list-heading">
          <div>
            <span className="va-section-kicker">CAMPAIGN MANAGEMENT</span>
            <h2>My Advertisements</h2>
            <p>Apne submitted aur approved campaigns ka status dekho.</p>
          </div>
          <button
            type="button"
            className="va-refresh-btn"
            onClick={loadAds}
            disabled={loading}
          >
            ↻ <span>Refresh</span>
          </button>
        </div>

        <div className="va-filters">
          <div className="va-search">
            <span>⌕</span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search campaigns..."
              aria-label="Search campaigns"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filter by status"
          >
            {["All", "Pending", "Approved", "Active", "Paused", "Rejected", "Completed"].map((status) => (
              <option value={status} key={status}>{status === "All" ? "All statuses" : status}</option>
            ))}
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            aria-label="Filter by type"
          >
            <option value="All">All types</option>
            {TYPES.map((type) => <option value={type} key={type}>{type}</option>)}
          </select>
        </div>

        <div className="va-table-wrap">
          <table className="va-table">
            <thead>
              <tr>
                <th>CAMPAIGN</th>
                <th>TYPE</th>
                <th>BUDGET</th>
                <th>CAMPAIGN PERIOD</th>
                <th>STATUS</th>
                <th className="va-actions-heading">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6">
                    <div className="va-loading">
                      <span className="va-spinner va-spinner-dark" />
                      Loading advertisements...
                    </div>
                  </td>
                </tr>
              ) : filteredAds.length === 0 ? (
                <tr>
                  <td colSpan="6">
                    <div className="va-empty">
                      <div className="va-empty-icon">▤</div>
                      <h3>{ads.length ? "No matching campaigns" : "Start your first campaign"}</h3>
                      <p>
                        {ads.length
                          ? "Search ya filters change karke dobara try karo."
                          : "Create an advertisement to start promoting your business."}
                      </p>
                      {!ads.length && (
                        <button
                          className="va-primary-btn"
                          onClick={() => setShowForm(true)}
                          type="button"
                        >
                          + Create Advertisement
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredAds.map((ad) => (
                  <tr key={ad._id}>
                    <td>
                      <div className="va-campaign-name">{ad.title}</div>
                      <div className="va-campaign-id">
                        ID: {ad._id?.slice(-8).toUpperCase() || "—"}
                      </div>
                    </td>
                    <td>
                      <span className="va-type-pill">{ad.adType}</span>
                    </td>
                    <td>
                      <span className="va-budget">{formatMoney(ad.budget)}</span>
                    </td>
                    <td>
                      <div className="va-date-range">
                        <span>{formatDate(ad.startDate)}</span>
                        <span className="va-date-arrow">→</span>
                        <span>{formatDate(ad.endDate)}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`va-status va-status-${getStatusClass(ad.status)}`}>
                        <span className="va-status-dot" />
                        {ad.status || "Pending"}
                      </span>
                      {ad.adminNote && (
                        <div className="va-admin-note" title={ad.adminNote}>
                          Admin: {ad.adminNote}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="va-row-actions">
                        {["Active", "Paused"].includes(ad.status) && (
                          <button
                            type="button"
                            className="va-action-btn"
                            disabled={busyId === ad._id}
                            onClick={() => changeStatus(ad)}
                            title={ad.status === "Active" ? "Pause campaign" : "Resume campaign"}
                          >
                            {ad.status === "Active" ? "Ⅱ Pause" : "▶ Resume"}
                          </button>
                        )}
                        {!["Active", "Paused"].includes(ad.status) && (
                          <span className="va-no-action">—</span>
                        )}
                        {!["Active", "Paused"].includes(ad.status) && (
                          <button
                            type="button"
                            className="va-delete-btn"
                            disabled={busyId === ad._id}
                            onClick={() => deleteAdvertisement(ad)}
                            title="Delete advertisement"
                          >
                            {busyId === ad._id ? "..." : "Delete"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="va-table-footer">
          <span>Showing <strong>{filteredAds.length}</strong> of <strong>{ads.length}</strong> campaigns</span>
          <span>Data synced with your account</span>
        </div>
      </section>

      <footer className="va-footer">
        <span>✦</span> BirdMyTrip Marketing Center
        <span className="va-footer-separator">•</span>
        Promote your business with confidence
      </footer>
    </main>
  );
}
